/**
 * StudyCreate — the /ereg/studies/new deep-link create form (plan §3.6).
 *
 * Prefills from ?protocol_number=&sponsor=&nickname=, honors the return_to
 * convention: on success it navigates back to the caller with
 * ?created_protocol_id=<id> appended so the caller can auto-link. A 409 from
 * the soft-uniqueness guard (§3.1) offers "link to the existing study instead"
 * (returns the match's id) or "create anyway".
 */
import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, FlaskConical, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useEreg } from '../contexts';
import * as eregApi from '../lib/api';
import { PageHeader, fieldCls, primaryBtnCls, secondaryBtnCls, useToast } from '../../ui';
import { sanitizeReturnTo, appendQueryParam } from '../../ui/returnTo';

interface DuplicateMatch {
  id: string;
  protocol_number: string;
  study_nickname?: string | null;
}

export function StudyCreate() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentSite, availableSites } = useAuth();
  const { refreshProtocols, setCurrentProtocol, setSiteMode } = useEreg();
  const { toast } = useToast();

  const returnTo = sanitizeReturnTo(searchParams.get('return_to'));
  const returnLabel = searchParams.get('return_label') || 'previous page';

  const [formData, setFormData] = useState({
    site_id: currentSite?.id || '',
    protocol_number: searchParams.get('protocol_number') || '',
    protocol_title: searchParams.get('nickname') || '',
    sponsor_name: searchParams.get('sponsor') || '',
    phase: '',
    therapeutic_area: '',
    irb_of_record: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [duplicates, setDuplicates] = useState<DuplicateMatch[] | null>(null);

  const sites = useMemo(() => availableSites || [], [availableSites]);

  const finish = (protocolId: string, created: boolean) => {
    if (returnTo) {
      navigate(appendQueryParam(returnTo, 'created_protocol_id', protocolId));
      return;
    }
    refreshProtocols();
    if (created) toast('Study created');
    // Land on the study's canonical URL — for the 409 "link instead" path the
    // study isn't in context yet, so StudyScope hydrates it from the id
    // (audit P-8: this used to strand on Folders with no study selected).
    navigate(`/ereg/studies/${protocolId}/folders`);
  };

  const submit = async (allowDuplicate = false) => {
    if (!formData.site_id || !formData.protocol_number.trim()) {
      toast('Site and protocol number are required', { type: 'error' });
      return;
    }
    setSubmitting(true);
    try {
      const created = await eregApi.createProtocol({
        ...formData,
        protocol_number: formData.protocol_number.trim(),
        ...(allowDuplicate ? { allow_duplicate: true } : {}),
      } as any);
      setSiteMode(false);
      setCurrentProtocol(created);
      finish(created.id, true);
    } catch (err: any) {
      if (err?.response?.status === 409 && err.response.data?.code === 'EREG_DUPLICATE_PROTOCOL') {
        setDuplicates(err.response.data.matches || []);
      } else {
        toast('Failed to create study', { type: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const labelCls = 'mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300';

  return (
    <div className="mx-auto w-full max-w-2xl">
      {returnTo && (
        <Link
          to={returnTo}
          className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 transition-colors duration-150 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to {returnLabel}
        </Link>
      )}

      <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        <PageHeader
          icon={FlaskConical}
          title="Create New Study"
          subtitle="Studies are created once, here in eReg — every tool links to the same record."
        />

        <form
          className="mt-4 space-y-3"
          onSubmit={(e) => { e.preventDefault(); submit(false); }}
        >
          <div>
            <label className={labelCls} htmlFor="study-site">Site *</label>
            <select
              id="study-site"
              value={formData.site_id}
              onChange={(e) => setFormData((f) => ({ ...f, site_id: e.target.value }))}
              className={`${fieldCls} w-full`}
              required
            >
              <option value="">Select a site…</option>
              {sites.map((site: any) => (
                <option key={site.id} value={site.id}>{site.name}</option>
              ))}
            </select>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className={labelCls} htmlFor="study-number">Protocol Number *</label>
              <input
                id="study-number"
                type="text"
                value={formData.protocol_number}
                onChange={(e) => { setFormData((f) => ({ ...f, protocol_number: e.target.value })); setDuplicates(null); }}
                className={`${fieldCls} w-full`}
                required
              />
            </div>
            <div>
              <label className={labelCls} htmlFor="study-title">Study Title</label>
              <input
                id="study-title"
                type="text"
                value={formData.protocol_title}
                onChange={(e) => setFormData((f) => ({ ...f, protocol_title: e.target.value }))}
                className={`${fieldCls} w-full`}
              />
            </div>
            <div>
              <label className={labelCls} htmlFor="study-sponsor">Sponsor</label>
              <input
                id="study-sponsor"
                type="text"
                value={formData.sponsor_name}
                onChange={(e) => setFormData((f) => ({ ...f, sponsor_name: e.target.value }))}
                className={`${fieldCls} w-full`}
              />
            </div>
            <div>
              <label className={labelCls} htmlFor="study-phase">Phase</label>
              <select
                id="study-phase"
                value={formData.phase}
                onChange={(e) => setFormData((f) => ({ ...f, phase: e.target.value }))}
                className={`${fieldCls} w-full`}
              >
                <option value="">—</option>
                <option value="I">Phase I</option>
                <option value="II">Phase II</option>
                <option value="III">Phase III</option>
                <option value="IV">Phase IV</option>
              </select>
            </div>
            <div>
              <label className={labelCls} htmlFor="study-ta">Therapeutic Area</label>
              <input
                id="study-ta"
                type="text"
                value={formData.therapeutic_area}
                onChange={(e) => setFormData((f) => ({ ...f, therapeutic_area: e.target.value }))}
                className={`${fieldCls} w-full`}
              />
            </div>
            <div>
              <label className={labelCls} htmlFor="study-irb">IRB of Record</label>
              <input
                id="study-irb"
                type="text"
                value={formData.irb_of_record}
                onChange={(e) => setFormData((f) => ({ ...f, irb_of_record: e.target.value }))}
                className={`${fieldCls} w-full`}
              />
            </div>
          </div>

          {duplicates && duplicates.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                Looks like this study already exists at this site
              </p>
              <ul className="mt-2 space-y-1.5">
                {duplicates.map((m) => (
                  <li key={m.id} className="flex flex-wrap items-center justify-between gap-2 text-sm text-amber-900 dark:text-amber-200">
                    <span className="truncate">{m.protocol_number}{m.study_nickname ? ` — ${m.study_nickname}` : ''}</span>
                    <button
                      type="button"
                      onClick={() => finish(m.id, false)}
                      className="inline-flex min-h-[44px] items-center rounded-lg border border-amber-300 px-2.5 py-1 text-xs font-medium text-amber-800 transition-colors duration-150 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-900/40 sm:min-h-0"
                    >
                      Link to this study instead
                    </button>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => submit(true)}
                disabled={submitting}
                className="mt-2 inline-flex min-h-[44px] items-center text-xs font-medium text-amber-700 underline transition-colors duration-150 hover:text-amber-900 dark:text-amber-400 dark:hover:text-amber-200 sm:min-h-0"
              >
                Create anyway (duplicate protocol number)
              </button>
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-1">
            <button
              type="button"
              className={secondaryBtnCls}
              onClick={() => (returnTo ? navigate(returnTo) : navigate('/ereg/dashboard'))}
            >
              Cancel
            </button>
            <button type="submit" className={primaryBtnCls} disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Create Study
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default StudyCreate;
